// Angular
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

// Material
import { MatDialog } from '@angular/material/dialog';

// Third-party
import {
  Observable,
  Subject,
  takeUntil,
  combineLatest,
  startWith,
  tap,
  fromEvent,
  take,
  Subscription,
  debounceTime,
  firstValueFrom,
} from 'rxjs';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Store } from '@ngrx/store';
import * as _ from 'lodash';

import {
  CitationService,
  SiteConfigService,
  WkKeywordService,
  WkReleaseNoteService,
} from '../../../services';
import { WkCommentsTabService } from '../../../services/wk-comments-tab.service';
import { WkCleanUpCommentsComponent } from '../../../dialogs/clean-up-comments/wk-clean-up-comments.dialog';
import { WkConfirmDialogComponent } from '../../../dialogs/wk-confirm-dialog/wk-confirm-dialog';
import { CommonConstants } from '../../../shared';
import {
  CONSTANTS,
  CommentTypeEnum,
  TrackChangesAndComments,
} from '../../../model';
import { FEATURE_FLAGS } from '../../../constants';
import { AppStore, getFeatureFlags, SnackbarInfoAction } from '../../../store';
import {
  getCurrentSelectedAIUMCNotes,
  setCurrentSelectedAIUMCNotes,
  getAiUMCReleaseNotes,
  getSelectedUMCReleaseNote,
  getUMCReleaseNotesLoading,
  INote,
  selectReleaseNote,
  removeAIUMCReleaseNote,
  resetAIUMCReleaseNoteState,
} from '../../../modules/ai-umc';
import {
  getSelectedCommentsTabIndex,
  setTrackchangeAndSuggestion,
} from '../../../../../src/app/modules/wk-document-decorator';
import { SiteConfig } from '../../../../../src/app/types';
import { SidePanelTabs } from '../../../../../src/app/enums';

const { TRACK_CHANGES_AND_COMMENTS_DATA } = CONSTANTS;

const {
  CK_THREAD_CLASS,
  comment,
  comments: { ckThreadContainer, ckSidebarItem },
  SKOS_EDITORIAL_NOTE,
  WIDGET_SELECTED,
  trackChanges: {
    acceptAllSuggestions,
    ckSuggestionWrapper,
    ckSuggestionWrapperActive,
    rejectAllSuggestions,
  },
  ACCEPT_ALL_SUGGESTIONS_CLASS,
  REJECT_ALL_SUGGESTIONS_CLASS,
  RELEASE_NOTES_TAG_NAME,
} = CommonConstants;

@Component({
  selector: 'wk-comments-tab',
  templateUrl: './wk-comments-tab.component.html',
  styleUrls: ['./wk-comments-tab.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: { class: 'wk-comments-tab' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WkCommentsTabComponent implements OnInit, OnDestroy {
  lastIdFocus = '';
  onLoadFlag = false;
  isNextRefAvailable = false;
  private readonly previousSequenceKeys = new Map<CommentTypeEnum, string>();
  private readonly contentChanged$ = new Subject<MutationRecord[]>();
  private scrollSubscription: Subscription;
  public releaseNotesTabLabel: string;
  public trackChangesTabLabel: string;
  public commentsTabLabel: string;
  public selectedNote$: Observable<any>;
  public umcReleaseNotesLoading$: Observable<boolean>;
  public aiUMCReleaseNotes$: Observable<INote[]>;
  public featureFlags$: Observable<{ [key: string]: boolean }>;
  private siteConfig: SiteConfig;
  private currentSelectedAIUMCNotes: INote[] = [];
  @ViewChild('referenceComments', { static: true })
  readonly referenceComments: ElementRef;

  @ViewChild('comments', { static: true })
  readonly comments: ElementRef;

  @ViewChild('trackchanges', { static: true })
  readonly trackchanges: ElementRef;

  @ViewChild('releasenotes', { static: true })
  readonly releasenotes: ElementRef;

  private readonly destroy$ = new Subject<void>();

  private readonly THREAD_ID = CommonConstants.comments.dataThreadId;
  private readonly SUGGESTION_WRAPPER =
    CommonConstants.trackChanges.ckSuggestionWrapper;
  readonly dialogRef: MatDialog;
  readonly actions = [
    { title: rejectAllSuggestions, class: REJECT_ALL_SUGGESTIONS_CLASS },
    { title: acceptAllSuggestions, class: ACCEPT_ALL_SUGGESTIONS_CLASS },
  ];
  private readonly siteConfigService: SiteConfigService;
  public threadTabs = [];
  public selectedTabIndex = 0;
  private readonly store: Store<AppStore>;
  public readonly GENAI_UPDATE_MANAGER_COMMENTS =
    FEATURE_FLAGS.GENAI_UPDATE_MANAGER_COMMENTS;
  private userScrolled = false;
  private isAIUMCLoading = false;
  private aiUMCNotes: INote[] = [];
  private pendingScrollId = '';
  public showScrollTop = false;
  private readonly SCROLL_THRESHOLD = 50;
  private readonly MAT_TAB_BODY_SELECTOR = 'mat-tab-body';
  constructor(
    @Inject(DOCUMENT)
    private readonly document: Document,
    readonly host: ElementRef,
    private readonly renderer: Renderer2,
    private readonly citationsService: CitationService,
    private readonly commentsTabService: WkCommentsTabService,
    private readonly keywordService: WkKeywordService,
    private readonly releaseNoteService: WkReleaseNoteService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly injector: Injector
  ) {
    this.dialogRef = this.injector.get<MatDialog>(MatDialog);
    this.siteConfigService =
      this.injector.get<SiteConfigService>(SiteConfigService);
    this.releaseNoteService =
      this.injector.get<WkReleaseNoteService>(WkReleaseNoteService);
    this.store = this.injector.get<Store<AppStore>>(Store);
  }

  ngOnInit() {
    this.onLoadFlag = true;
    this.aiUMCReleaseNotes$ = this.store.select<any[]>(getAiUMCReleaseNotes);
    this.selectedNote$ = this.store.select(getSelectedUMCReleaseNote);
    this.umcReleaseNotesLoading$ = this.store.select(getUMCReleaseNotesLoading);
    this.featureFlags$ = this.store.select(getFeatureFlags);
    this.umcReleaseNotesLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isloading) => (this.isAIUMCLoading = isloading));
    this.aiUMCReleaseNotes$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notes) => {
        const hadNoNotes = _.isEmpty(this.aiUMCNotes);
        const hasNotesNow = !_.isEmpty(notes);
        this.aiUMCNotes = notes;
        // When a fresh batch of generated notes populates the list (empty -> filled),
        // pin the Release Notes panel to the top so the first note is visible.
        if (hadNoNotes && hasNotesNow) {
          setTimeout(() => this.scrollToTop());
        }
      });

    this.store
      .select(getCurrentSelectedAIUMCNotes)
      .pipe(takeUntil(this.destroy$))
      .subscribe((notes) => {
        this.currentSelectedAIUMCNotes = notes ?? [];
        // When the selected notes store is reset to an empty array, ensure the
        // mat-checkbox [checked] bindings re-evaluate so all checkmarks are cleared.
        if (_.isEmpty(this.currentSelectedAIUMCNotes)) {
          this.changeDetectorRef.markForCheck();
        }
      });

    combineLatest([
      this.citationsService.selectedIdCommentDoc.pipe(startWith('')),
      this.commentsTabService.editorInstanceChanged$,
    ])
      .pipe(
        tap({
          next: ([commentId, { editableElement, suggestions }]) => {
            this.arrangeComments(editableElement, suggestions);
            const hasActiveSuggestion =
              this.trackchanges?.nativeElement.querySelector(
                `.${ckSuggestionWrapperActive}`
              );

            const threadPresent =
              !!this.trackchanges?.nativeElement.querySelector(
                `[${this.THREAD_ID}='${commentId}']`
              );

            // Subscribe to scroll only once to avoid listener leak.
            // Scroll tracking (userScrolled gate + scroll-to-top button) is only relevant for the
            // Release Notes tab; for Track Changes / Editorial tabs we always want clicks in the
            // document to scroll the matching comment into view.
            if (!this.scrollSubscription) {
              const scrollContainer = this.host.nativeElement.closest(
                this.MAT_TAB_BODY_SELECTOR
              );
              if (scrollContainer) {
                this.scrollSubscription = fromEvent(scrollContainer, 'scroll')
                  .pipe(takeUntil(this.destroy$))
                  .subscribe(() => {
                    if (this.selectedTabIndex === 2) {
                      this.userScrolled = true;
                      this.showScrollTop =
                        scrollContainer.scrollTop > this.SCROLL_THRESHOLD;
                    } else {
                      this.showScrollTop = false;
                    }
                    this.changeDetectorRef.markForCheck();
                  });
              }
            }
            const shouldAutoScroll =
              !this.userScrolled && !(hasActiveSuggestion && !threadPresent);

            if (shouldAutoScroll) {
              this.scrollToComment(commentId);
            }
          },
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
    if (!this.keywordService.isNavigation.value) {
      this.citationsService.isOpenCommentsTab.next(true);
    }
    this.citationsService.idToFocus
      .pipe(takeUntil(this.destroy$))
      .subscribe((idComment) => {
        this.setFocusCommentId(idComment);
      });
    // Always scroll to the matching sidebar item when something is clicked in
    // the document, regardless of the user's prior manual scroll position.
    this.citationsService.selectedIdCommentDoc
      .pipe(takeUntil(this.destroy$))
      .subscribe((commentId) => {
        // Always scroll for the active tab: 0 (Track Changes), 1 (Editorial), 2 (Release Notes)
        if (
          (this.selectedTabIndex === 0 ||
            this.selectedTabIndex === 1 ||
            this.selectedTabIndex === 2) &&
          commentId
        ) {
          this.scrollToComment(commentId);
        }
      });
    this.store
      .select(getSelectedCommentsTabIndex)
      .pipe(takeUntil(this.destroy$))
      .subscribe((index) => {
        this.selectedTabIndex = index;
        if (!this.selectedTabIndex) {
          this.updateTabStatus();
        }
        this.changeDetectorRef.markForCheck();
        // When the tab switches in response to a document click, the target
        // sidebar item lives in the newly-activated tab body which is not in
        // the DOM yet. Retry the pending scroll once the tab content is rendered
        // so the first click highlights the matching comment/suggestion.
        if (this.pendingScrollId) {
          const idToScroll = this.pendingScrollId;
          setTimeout(() => this.scrollToComment(idToScroll));
        }
      });

    this.siteConfig = this.siteConfigService.getCurrentSiteObject(
      localStorage.getItem('currentSite')
    );

    _.forIn(this.siteConfig.commentTab, (_value, _key, obj) => {
      this.threadTabs.push(obj);
    });
    this.threadTabs = this.setPrefixForTabs(this.threadTabs);

    this.contentChanged$
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe((event) => {
        this.onContentChanged(event);
      });

    this.commentsTabService.toggle$.subscribe((value: boolean) => {
      _.forEach(this.threadTabs, (tab) => {
        if (tab.label === this.trackChangesTabLabel) {
          tab.disabled = value;
        }
      });
      if (value) {
        this.setDisplayTab(value);
      } else {
        this.refreshAnnotationsAfterInlineToggleOff();
      }
    });
  }

  public readonly tabTrackByFn = (index: number, item): string =>
    index + item.label;

  public async onSelectionChange(id): Promise<void> {
    const notes = await firstValueFrom(this.aiUMCReleaseNotes$.pipe(take(1)));
    const note = _.find(notes, (selectedNote: INote) => selectedNote.id === id);
    this.currentSelectedAIUMCNotes = [...this.currentSelectedAIUMCNotes, note];
    this.store.dispatch(selectReleaseNote({ note }));
    this.store.dispatch(
      setCurrentSelectedAIUMCNotes({
        currentSelectedAIUMCNotes: this.currentSelectedAIUMCNotes,
      })
    );
  }

  public isAIUMCNoteSelected(id: string): boolean {
    return _.some(this.currentSelectedAIUMCNotes, (note) => note?.id === id);
  }

  contentChanged(event) {
    this.contentChanged$.next(event);
  }

  private onContentChanged(event) {
    this.commentsTabService.contentChangedSubject.next();
    if (
      this.comments?.nativeElement.querySelectorAll(
        '.ck-comment__main.ck-annotation__main'
      ).length > 0
    ) {
      this.host.nativeElement.querySelector(
        `[id="clean-up-comments"]`
      ).disabled = false;
    }
    // Check if event is a new thread creation event
    const isEmptyCommentThread = _.find(event, (record) => {
      const addedNodesExist = record.addedNodes.length > 0;
      const isSidebarItem = record.target.classList?.contains(ckSidebarItem);
      const isChildOfSidebarItem = record.target?.closest?.(
        `.${ckSidebarItem}`
      );
      const isNotChildListType = record.type !== 'childList';
      return (
        (addedNodesExist && (isSidebarItem || isChildOfSidebarItem)) ||
        isNotChildListType
      );
    });
    this.updateTabStatus();
    const {
      firstPositionOfSelection,
      isCommentsTabOpened,
      lastPositionOfSelection,
    } = this.commentsTabService;
    const { model, plugins } =
      this.citationsService.ckeditorInstanceFromEditorComp;
    model.change((writer) => {
      firstPositionOfSelection &&
        lastPositionOfSelection &&
        writer.setSelection(
          writer.createRange(firstPositionOfSelection, lastPositionOfSelection)
        );
    });

    if (!isEmptyCommentThread) {
      isCommentsTabOpened && this.setDisplayTab();
      const suggestionsData = plugins.get('TrackChanges').getSuggestions({
        skipNotAttached: true,
        toJSON: true,
        skipEmpty: true,
      });
      const commentThreadsData = plugins
        .get('CommentsRepository')
        ?.getCommentThreads({
          skipNotAttached: true,
          toJSON: true,
          skipEmpty: true,
        });
      this.store.dispatch(
        setTrackchangeAndSuggestion({
          trackChangeData: {
            commentThreads: commentThreadsData,
            suggestions: suggestionsData,
          },
        })
      );
    }
  }

  setFocusCommentId(idComment: string) {
    const elementSuggestion =
      this.onLoadFlag &&
      this.trackchanges?.nativeElement.querySelector(
        `[${this.THREAD_ID}='${idComment}']`
      );
    const elementComment =
      this.onLoadFlag &&
      this.comments?.nativeElement.querySelector(
        `[${this.THREAD_ID}='${idComment}']`
      );
    const elementReleaseNote = this.releasenotes?.nativeElement.querySelector(
      `[${this.THREAD_ID}='${idComment}']`
    );
    (elementComment || elementReleaseNote)
      ?.querySelector(`${CK_THREAD_CLASS}`)
      ?.focus({ preventScroll: true });
    if (elementSuggestion) {
      const elementContent = elementSuggestion.querySelector(
        `.${this.SUGGESTION_WRAPPER}`
      );
      elementContent?.focus();
      this.onLoadFlag = false;
      this.citationsService.isCommentsTabLoaded.next(true);
    }
    if (elementComment) {
      const elementContent = elementComment.querySelector(`${CK_THREAD_CLASS}`);
      const replyPlaceholder = 'Reply...';
      const replyElement = elementContent?.querySelector(
        `[data-placeholder='${replyPlaceholder}']`
      );
      replyElement && this.citationsService.isCommentsTabLoaded.next(true);
      this.onLoadFlag = false;
    }
    this.citationsService.isCommentReadyForFocus.next(false);
  }

  protected clickSectionComment(event?: KeyboardEvent): void {
    _.forEach(
      this.citationsService.ckeditorInstanceFromEditorComp.editing.view.domRoots
        .get('main')
        .querySelectorAll(`.${WIDGET_SELECTED}[rel="${SKOS_EDITORIAL_NOTE}"]`),
      (releaseNote: Element) => {
        releaseNote.classList.remove(WIDGET_SELECTED);
      }
    );
    const elementThreadActive =
      this.document.querySelector(`${CK_THREAD_CLASS}--active`) ||
      this.document.querySelector(`.${this.SUGGESTION_WRAPPER}--active`);
    if (elementThreadActive) {
      const isTrackChangesComments =
        !elementThreadActive.classList.contains('ck-thread');
      const hasAnySubComments = Boolean(
        elementThreadActive.querySelector(`${CK_THREAD_CLASS}__comments`)
          ?.children?.length
      );
      // A plain release note is a .ck-thread with no replies. Detect it by
      // checking whether the active thread sits inside the Release Notes
      // sidebar container, so its highlight pipeline (selectedSidebarCommentId
      // -> editor.scrollToComment -> classList.add(WIDGET_SELECTED)) still
      // runs after clickSectionComment wipes existing highlights.
      const isReleaseNoteThread = Boolean(
        this.releasenotes?.nativeElement?.contains(elementThreadActive)
      );
      if (isTrackChangesComments || hasAnySubComments || isReleaseNoteThread) {
        const threadId = elementThreadActive.getAttribute(this.THREAD_ID);
        this.citationsService.selectedSidebarCommentId.next(threadId);
      }
    }
    if (event) {
      this.releaseNoteService.keyUpEvent.next(event);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private arrangeComments(
    editableElement: HTMLElement,
    suggestions: { id: string }[]
  ) {
    const referenceCommentCollection =
      this.referenceComments?.nativeElement.querySelectorAll(
        `.${ckSidebarItem}`
      );
    const { COMMENT, SUGGESTION, RELEASENOTES } = CommentTypeEnum;

    // Editorial and Release Notes: use original per-element ordering
    [RELEASENOTES, COMMENT].forEach((commentType) => {
      const commentSequence = this.commentsTabService.getCommentSequence(
        commentType,
        editableElement,
        suggestions,
        this.citationsService.ckeditorInstanceFromEditorComp
      );

      const commentElements = this.getCommentElements(
        `${CK_THREAD_CLASS}`,
        referenceCommentCollection
      );

      this.arrangeCommentSequenceOriginal(
        commentElements,
        commentSequence,
        commentType
      );
    });

    // Suggestions: use optimized path with sequence caching + DocumentFragment batching
    const suggestionSequence = this.commentsTabService.getCommentSequence(
      SUGGESTION,
      editableElement,
      suggestions,
      this.citationsService.ckeditorInstanceFromEditorComp
    );

    const sequenceKey = suggestionSequence
      .map(([_sequenceIndex, id]) => id)
      .join(',');
    const previousSequenceKey = this.previousSequenceKeys.get(SUGGESTION);
    const hasSuggestionsToRender = suggestionSequence.length > 0;
    const hasRenderedSuggestions =
      this.trackchanges?.nativeElement?.children?.length > 0;
    // Reorder suggestion DOM only when the suggestion id sequence changed or when
    // the container still needs its initial render after inline mode transitions.
    const shouldRefreshSuggestions =
      previousSequenceKey !== sequenceKey ||
      (hasSuggestionsToRender && !hasRenderedSuggestions);

    if (shouldRefreshSuggestions) {
      // Caching the rendered sequence avoids repeating arrangeCommentSequence for
      // identical suggestion snapshots emitted by subsequent refresh triggers.
      this.previousSequenceKeys.set(SUGGESTION, sequenceKey);

      const suggestionElements = this.getCommentElements(
        `.${this.SUGGESTION_WRAPPER}`,
        referenceCommentCollection
      );

      this.arrangeCommentSequence(
        suggestionElements,
        suggestionSequence,
        SUGGESTION
      );
    }

    this.citationsService.ckeditorInstanceFromEditorComp?.plugins
      ?.get('Annotations')
      .refreshVisibility();
    // This handles the case where scrollToComment was called before the sidebar element existed (e.g. new release note at end of document).
    if (this.pendingScrollId) {
      this.scrollToComment(this.pendingScrollId);
    }
  }

  private arrangeCommentSequence(
    commentList: HTMLElement[],
    sequence,
    commentType: CommentTypeEnum
  ) {
    const targetElement =
      commentType === CommentTypeEnum.RELEASENOTES
        ? this.releasenotes.nativeElement
        : commentType === CommentTypeEnum.COMMENT
        ? this.comments.nativeElement
        : this.trackchanges.nativeElement;

    // Merge new elements with existing children, deduplicate
    const existingChildren = Array.from<HTMLElement>(targetElement.children);
    const seen = new Set<HTMLElement>();
    const allComments = [...existingChildren, ...commentList].filter((el) => {
      if (seen.has(el)) {
        return false;
      }
      seen.add(el);
      return true;
    });

    // Build ordered list using sequence index
    const orderedComments = allComments.map((commentElement) => {
      const index = sequence.findIndex(
        ([_sequenceIndex, id]) =>
          id === commentElement.getAttribute(this.THREAD_ID)
      );
      return { comment: commentElement, index };
    });

    // Sort by sequence order (unmatched items go to end)
    orderedComments.sort((a, b) => {
      if (a.index === -1) {
        return 1;
      }
      if (b.index === -1) {
        return -1;
      }
      return a.index - b.index;
    });

    // Use DocumentFragment to batch all DOM insertions into a single operation
    const fragment = this.document.createDocumentFragment();
    orderedComments.forEach(({ comment: commentElement }) => {
      fragment.appendChild(commentElement);
    });

    // Single DOM mutation: append the entire fragment at once
    targetElement.appendChild(fragment);

    this.citationsService.isCommentReadyForFocus.next(true);
  }

  /**
   * Original per-element ordering for Editorial and Release Notes.
   */
  private arrangeCommentSequenceOriginal(
    commentList: HTMLElement[],
    sequence,
    commentType: CommentTypeEnum
  ) {
    commentList.forEach((commentElement) => {
      const targetElement =
        commentType === CommentTypeEnum.RELEASENOTES
          ? this.releasenotes.nativeElement
          : this.comments.nativeElement;

      const currentSequence = sequence.findIndex(
        ([_sequenceIndex, id]) =>
          id === commentElement.getAttribute(this.THREAD_ID)
      );

      const nextSequence = sequence[currentSequence + 1]
        ? sequence[currentSequence + 1]
        : null;

      this.renderBasedOnSequence(
        sequence,
        commentElement,
        commentType,
        targetElement,
        currentSequence,
        nextSequence
      );
    });

    this.citationsService.isCommentReadyForFocus.next(true);
  }

  private getrefchild(targetElement, nextSequence) {
    const [, nextCommentId] = nextSequence;
    return targetElement.querySelector(
      `[${this.THREAD_ID}='${nextCommentId}']`
    );
  }

  private renderBasedOnSequence(
    _sequence,
    commentElement,
    _commentType: CommentTypeEnum,
    targetElement,
    currentSequence,
    nextSequence
  ) {
    if (currentSequence > -1 && Boolean(nextSequence)) {
      const refChild = this.getrefchild(targetElement, nextSequence);
      this.renderer.insertBefore(targetElement, commentElement, refChild);
    } else {
      this.renderer.appendChild(targetElement, commentElement);
    }
  }

  private assignThreadIdentifier(
    commentElement: HTMLElement,
    selectorType: string
  ): void {
    const thread = commentElement.querySelector(selectorType);
    const threadId = thread.getAttribute(this.THREAD_ID);

    // Adds data-releasenotes attribute for threadid belonging to release note in document
    const { commentThreads } = JSON.parse(
      localStorage.getItem(TRACK_CHANGES_AND_COMMENTS_DATA)
    ) as TrackChangesAndComments;

    if (
      this.citationsService.ckeditorInstanceFromEditorComp.editing.view.domRoots
        .get('main')
        .querySelector(`[rel="${SKOS_EDITORIAL_NOTE}"][id="${threadId}"]`) ||
      commentThreads.find((commentThread) => {
        return commentThread.threadId === threadId;
      })?.comments[0]?.attributes.commentType ===
        this.siteConfig.commentTab.releasenote.commentType
    ) {
      this.renderer.setAttribute(
        commentElement,
        CommonConstants.dataReleaseNotes,
        threadId
      );
    }
    this.renderer.setAttribute(commentElement, this.THREAD_ID, threadId);
  }

  private getCommentElements(
    selectorType: string,
    referenceComments: HTMLElement[] = [],
    result: any[] = []
  ): HTMLElement[] {
    referenceComments.forEach((rComment) => {
      const target = rComment.querySelector(selectorType);
      if (target) {
        const parent = target.closest(`.${ckSidebarItem}`);
        // Check if data-releasenotes attribute is already present and ignores
        if (parent && !parent.hasAttribute(CommonConstants.dataReleaseNotes)) {
          this.assignThreadIdentifier(parent as HTMLElement, selectorType);
          result.push(parent);
        }
      }
    });

    return result;
  }

  /**
   * Scrolls the comments panel to bring the given comment thread into view.
   * If the element is not yet in the DOM, stores the ID in pendingScrollId
   * so arrangeComments can retry once the element is rendered.
   */
  private scrollToComment(commentId: string) {
    const focusElement =
      this.trackchanges.nativeElement.querySelector(
        `[${this.THREAD_ID}='${commentId}']`
      ) ||
      this.comments.nativeElement.querySelector(
        `[${this.THREAD_ID}='${commentId}']`
      ) ||
      this.releasenotes.nativeElement.querySelector(
        `[${this.THREAD_ID}='${commentId}']`
      );

    if (!focusElement) {
      // Element not in DOM yet; defer scroll until arrangeComments places it
      this.pendingScrollId = commentId;
      return;
    }

    this.pendingScrollId = '';
    const parent = this.host.nativeElement.closest(this.MAT_TAB_BODY_SELECTOR);
    if (parent) {
      const currentTop = parent.scrollTop || 0;
      const { top, height } = focusElement.getBoundingClientRect();
      parent.scrollTop = currentTop + top - height;
    }
  }

  public scrollToTop(): void {
    this.userScrolled = true;
    this.pendingScrollId = '';
    const parent = this.host.nativeElement.closest(this.MAT_TAB_BODY_SELECTOR);
    if (parent) {
      parent.scrollTop = 0;
    }
    this.showScrollTop = false;
  }

  public clearAllUMCComments(): void {
    this.openUMCDeleteConfirm('CLEAR_ALL_MESSAGE');
  }

  public onDeleteUMCComment(event: MouseEvent, note: INote): void {
    event.stopPropagation();
    event.preventDefault();
    this.openUMCDeleteConfirm('MESSAGE', note);
  }

  private openUMCDeleteConfirm(
    messageKey: 'MESSAGE' | 'CLEAR_ALL_MESSAGE',
    note?: INote
  ): void {
    const headingKey =
      messageKey === 'CLEAR_ALL_MESSAGE' ? 'CLEAR_ALL_HEADING' : 'HEADING';
    const dialog = this.dialogRef.open(WkConfirmDialogComponent, {
      data: {
        title: `APP.COMMENTS.DELETE_UMC_COMMENT.${headingKey}`,
        message: `APP.COMMENTS.DELETE_UMC_COMMENT.${messageKey}`,
        noLabel: 'APP.COMMENTS.DELETE_UMC_COMMENT.CANCEL',
        yesLabel: 'APP.COMMENTS.DELETE_UMC_COMMENT.DELETE',
        yesButtonClass: 'wk-button wk-button-danger',
      },
      minWidth: '200px',
    });
    dialog
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (!result) {
          return;
        }
        if (note) {
          const notes = this.aiUMCNotes.filter(
            (umcNote) => umcNote.id !== note.id
          );
          this.store.dispatch(removeAIUMCReleaseNote({ notes }));
          const remainingNotes = this.currentSelectedAIUMCNotes.filter(
            (selectedNote) => selectedNote.id !== note.id
          );
          this.store.dispatch(
            setCurrentSelectedAIUMCNotes({
              currentSelectedAIUMCNotes: remainingNotes,
            })
          );
          this.store.dispatch(
            new SnackbarInfoAction(
              'APP.COMMENTS.DELETE_UMC_COMMENT.DELETE_SUCCESS'
            )
          );
        } else {
          this.store.dispatch(
            resetAIUMCReleaseNoteState({
              notes: [],
              note: null,
              isCreating: false,
            })
          );
          this.store.dispatch(
            new SnackbarInfoAction(
              'APP.COMMENTS.DELETE_UMC_COMMENT.CLEAR_ALL_SUCCESS'
            )
          );
        }
      });
  }

  cleanUpComments() {
    const dialog = this.dialogRef.open(WkCleanUpCommentsComponent, {
      height: '285px',
      width: '419px',
      panelClass: 'wk-delete-citation-dialog',
      autoFocus: false,
    });
    dialog
      .afterClosed()
      .pipe(take(1))
      .subscribe((data) => {
        if (data === 'deleteComments') {
          this.citationsService.deleteComments.next(true);
        }
      });
  }

  acceptDiscardSuggestions(event: string) {
    this.citationsService.acceptRejectSuggestions.next(event);
  }

  public noCommentsAvailable(): boolean {
    try {
      const commentNativeElements =
        this.comments?.nativeElement.querySelectorAll(
          '.ck-comment__main.ck-annotation__main'
        );
      const commentsInRepository =
        this.citationsService.getCommentsInRepository(comment);
      return (
        commentNativeElements?.length === 0 &&
        commentsInRepository?.length === 0
      );
    } catch (error) {
      return true;
    }
  }

  public noTrackChangesAvailable(): boolean {
    try {
      const trackChangesNativeElements =
        this.trackchanges?.nativeElement.querySelectorAll?.(
          `.${ckSuggestionWrapper}`
        );
      const trackChangesInRepository =
        this.citationsService.getTrackChangesInRepository();
      return (
        !!this.commentsTabService.isInlineToggled ||
        (trackChangesNativeElements?.length === 0 &&
          trackChangesInRepository?.length === 0)
      );
    } catch (error) {
      return true;
    }
  }

  public noReleaseNotesAvailable(): boolean {
    try {
      const releaseNotesNativeElements =
        !this.releasenotes?.nativeElement.querySelectorAll(
          `.${ckThreadContainer}`
        )?.length;
      const releaseNotesInRepository =
        !this.citationsService.getCommentsInRepository(RELEASE_NOTES_TAG_NAME)
          .length;

      if (this.aiUMCNotes.length) {
        return false;
      }

      if (this.isAIUMCLoading) {
        return !this.isAIUMCLoading;
      }

      return releaseNotesNativeElements && releaseNotesInRepository;
    } catch (error) {
      return true;
    }
  }

  private setPrefixForTabs(threadTabs): Array<{ label: string }> {
    const tabs = [];
    _.forIn(threadTabs[0], (value, key) => {
      tabs.push({
        label: value.label,
      });

      switch (key) {
        case 'editorial':
          this.commentsTabLabel = value.label;
          break;
        case 'trackchange':
          this.trackChangesTabLabel = value.label;
          break;
        case 'releasenote':
          this.releaseNotesTabLabel = value.label;
          break;
      }
    });
    return tabs;
  }

  /**
   * Enables / Disables tabs under comments panel i.e. Track changes tab / Editorial tab / Release notes tab.
   * @private
   * @returns { void }
   */
  private updateTabStatus(): void {
    const tabConditions = [
      {
        label: this.commentsTabLabel,
        condition: () => this.noCommentsAvailable(),
      },
      {
        label: this.trackChangesTabLabel,
        condition: () => this.noTrackChangesAvailable(),
      },
      {
        label: this.releaseNotesTabLabel,
        condition: () => this.noReleaseNotesAvailable(),
      },
    ];
    _.forEach(this.threadTabs, (threadTab) => {
      const tabCondition = _.find(
        tabConditions,
        (condition) => condition.label === threadTab.label
      );
      if (tabCondition) {
        threadTab.disabled = tabCondition.condition();
      }
    });
  }

  /**
   * Switch to first enabled tab if current tab doesn't contains any thread.
   * @private
   * @returns { void }
   */
  private setDisplayTab(value = false): void {
    if (value) {
      this.updateTabStatus();
      if (!this.threadTabs[0].disabled) {
        this.setSelectedTab(0);
        this.commentsTabService.dispatchSelectedCommentsTabIndex(0);
      }
    }

    if (this.threadTabs[this.selectedTabIndex].disabled) {
      const firstEnabledTab = _.findIndex(this.threadTabs, { disabled: false });
      if (firstEnabledTab >= 0) {
        this.setSelectedTab(firstEnabledTab);
        value &&
          this.commentsTabService.dispatchSelectedCommentsTabIndex(
            firstEnabledTab
          );
        this.citationsService.isOpenCommentsTab.next(true);
      } else {
        const suppressFallbackToContent =
          this.selectedTabIndex === 2 &&
          (this.commentsTabService.isReleaseNoteAdded || this.isAIUMCLoading);

        if (suppressFallbackToContent) {
          return;
        }

        this.citationsService.changeSidePanelTabTo(SidePanelTabs.Content);
      }
    }
    this.citationsService.ckeditorInstanceFromEditorComp?.plugins
      ?.get('Annotations')
      .refreshVisibility();
  }

  private refreshAnnotationsAfterInlineToggleOff(): void {
    const editor = this.citationsService.ckeditorInstanceFromEditorComp;
    if (!editor) {
      return;
    }
    const editableElement = editor.editing?.view?.domRoots?.get('main');
    const suggestions =
      editor.plugins?.get('TrackChanges')?.getSuggestions({
        skipNotAttached: true,
      }) || [];

    if (editableElement) {
      this.arrangeComments(editableElement, suggestions);
    }

    this.setDisplayTab();
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Updates selected tab index.
   * @private
   * @param { number } index
   * @returns { void }
   */
  private setSelectedTab(index: number): void {
    this.selectedTabIndex = index || 0;
  }

  /**
   * Set selected tab in store upon tab change event.
   * @public
   * @param { MatTabChangeEvent } event
   * @returns { void }
   */
  public onTabChange(eventOrIndex: MatTabChangeEvent | number): void {
    const index =
      typeof eventOrIndex === 'number'
        ? eventOrIndex
        : eventOrIndex?.index ?? 0;
    // Reset scroll-tracking state when leaving/entering tabs so the Release Notes
    // userScrolled gate doesn't carry over across tab switches.
    this.userScrolled = false;
    this.showScrollTop = false;
    this.commentsTabService.dispatchSelectedCommentsTabIndex(index);
  }
}
